import type {
  CurrentData,
  EstimatedCoverage,
  ProductCoverage,
} from "../types";

function coverageMetricFromApi(value: unknown): number {
  if (value === null || value === undefined) {
    return NaN;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function utcIsoWeek(d: Date): number {
  const date = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
  date.setUTCDate(date.getUTCDate() + 3 - ((date.getUTCDay() + 6) % 7));
  const week1 = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getUTCDay() + 6) % 7)) /
        7,
    )
  );
}

function targetDateFromApi(raw: unknown): EstimatedCoverage["targetDate"] {
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s) {
      const d = new Date(s);
      if (Number.isFinite(d.getTime())) {
        return {
          year: d.getUTCFullYear(),
          month: d.getUTCMonth() + 1,
          week: Math.max(1, utcIsoWeek(d)),
        };
      }
    }
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const y = Number(o.year);
    const m = Number(o.month);
    const w = Number(o.week);
    return {
      year: Number.isFinite(y) ? Math.trunc(y) : 0,
      month: Number.isFinite(m) ? Math.trunc(m) : 0,
      week: Number.isFinite(w) ? Math.trunc(w) : 0,
    };
  }
  return { year: 0, month: 0, week: 0 };
}

function coverageCurrentFromApi(raw: unknown): CurrentData {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    return {
      inventory: coverageMetricFromApi(o.inventory),
      sales: coverageMetricFromApi(o.sales),
      forecast: coverageMetricFromApi(o.forecast),
    };
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return {
      inventory: raw,
      sales: NaN,
      forecast: NaN,
    };
  }
  return {
    inventory: NaN,
    sales: NaN,
    forecast: NaN,
  };
}

/** Mapea filas del GET v2 o snapshot de sesión al estado del simulador. */
export function mapCoverageListFromApi(
  data: Array<Record<string, unknown>>,
): ProductCoverage[] {
  return data.map((product) => {
    const _product: ProductCoverage = {
      productProps: {
        id: Number(product.id),
        erpCode: String(product.erp_code ?? ""),
        description: String(product.description ?? ""),
        category: String(product.category ?? ""),
        line: String(product.line ?? ""),
        subLine: String(product.sub_line ?? ""),
        costoFobXUnidad:
          product.costo_fob_x_unidad != null
            ? String(product.costo_fob_x_unidad)
            : null,
        costoFobXBulto:
          product.costo_fob_x_bulto != null
            ? String(product.costo_fob_x_bulto)
            : null,
        unidadXBulto: (product.unidad_x_bulto as number | null) ?? null,
        cbmXBulto:
          product.cbm_x_bulto !== undefined && product.cbm_x_bulto !== null
            ? Number(product.cbm_x_bulto)
            : null,
        pesoXBulto:
          product.peso_x_bulto !== undefined && product.peso_x_bulto !== null
            ? Number(product.peso_x_bulto)
            : null,
        coverageDeviationReason: String(product.coverage_deviation_reason ?? ""),
      },
      coverage: [],
    };
    const rows = Array.isArray(product.coverage) ? product.coverage : [];
    _product.coverage = rows.map((coverage) => {
      const row = coverage as Record<string, unknown>;
      const events = (row.events ?? {}) as Record<string, unknown>;
      return {
        targetDate: targetDateFromApi(row.target_date),
        current: coverageCurrentFromApi(row.current),
        estimatedUnits: row.estimated_units as EstimatedCoverage["estimatedUnits"],
        coverageTime: row.coverage_time as EstimatedCoverage["coverageTime"],
        events: {
          alert: events.alert as EstimatedCoverage["events"]["alert"],
          incomes: events.incomes as EstimatedCoverage["events"]["incomes"],
          outcomes: events.outcomes as EstimatedCoverage["events"]["outcomes"],
        },
      } satisfies EstimatedCoverage;
    });
    return _product;
  });
}
